// IMPORTAÇÃO de VISITAS HISTÓRICAS (aba "Visitas" da planilha de roteirização — o histórico "Tebaldi"
// anterior ao app). Serve para semear a ÚLTIMA VISITA de clientes já visitados antes do app existir;
// sem isso, o motor semanal os trata como "nunca visitados" (prioridade máxima indevida).
// Extrai o CODPARC de "CODPARC - NOME", pega a MAIOR data de CheckIn por CODPARC (aceita 'dd/mm/aaaa',
// 'dd/mm/aaaa, HH:MM' e serial do Excel) e envia JSON. A escrita (clients.lastCheckinAt, SÓ se a data
// for MAIS RECENTE que a atual) é server-side pela callable importarVisitasHistoricas (Admin SDK) —
// nunca sobrescreve um check-in real mais novo.
import * as XLSX from 'xlsx';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';

const soDigitos = (s: unknown): string => String(s ?? '').replace(/\D/g, '');
const normKey = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

// Converte serial do Excel (dias desde 1899-12-30) para Date local ao meio-dia (evita drift de fuso).
function fromSerial(serial: number): Date | null {
  if (!Number.isFinite(serial) || serial < 20000 || serial > 90000) return null; // ~1954..2146
  const ms = Math.round((serial - 25569) * 86400000); // 25569 = dias entre 1899-12-30 e 1970-01-01
  const u = new Date(ms);
  return new Date(u.getUTCFullYear(), u.getUTCMonth(), u.getUTCDate(), 12, 0, 0);
}
// Aceita string 'dd/mm/aaaa' (com ou sem ', HH:MM'), 'aaaa-mm-dd' e serial do Excel. Só a DATA importa.
// IMPORTANTE: valida os componentes (o Date do JS NORMALIZA overflow — '32/13/2026' viraria 01/02/2027;
// '29/02/2026' viraria 01/03) e barra datas implausíveis. Uma data-lixo no futuro venceria o MAX por
// CODPARC e gravaria lastCheckinAt no futuro → o motor congelaria o cliente fora do roteiro. Datas
// rejeitadas retornam null e caem em `semData` (visíveis no resumo) em vez de corromper a cadência.
function parseDataVisita(v: unknown): Date | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return fromSerial(v);
  const s = String(v).trim();
  let y = 0, mo = 0, dia = 0;
  let m = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) { dia = +m[1]; mo = +m[2]; y = +m[3]; }
  else {
    m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    y = +m[1]; mo = +m[2]; dia = +m[3];
  }
  const d = new Date(y, mo - 1, dia, 12, 0, 0);
  // Round-trip: rejeita overflow (32/13, 31/04, 29/02 em ano não bissexto, dia 0…).
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== dia) return null;
  // Faixa plausível: nada no futuro (typo de ano / célula em mm/dd invertida) nem antes de 2015.
  if (d.getTime() > Date.now() + 86400000 || y < 2015) return null;
  return d;
}

export interface LinhaVisita { codparc: string; dataVisitaISO: string; }
export interface ResumoImportVisitas {
  total: number;        // linhas de dados na aba
  semCodparc: number;   // linhas sem CODPARC parseável em "Cliente"
  semData: number;      // linhas com CODPARC mas data não parseável
  codparcs: number;     // CODPARC distintos enviados (1 data máx. cada)
  casados: number;      // CODPARC que casaram com cliente(s) na base
  semMatch: number;     // CODPARC sem cliente correspondente
  atualizados: number;  // documentos com lastCheckinAt atualizado
  preservados: number;  // documentos preservados (check-in real igual/mais novo venceu)
}

// Acha a aba e a LINHA de cabeçalho (tolera o offset do título mesclado nas 2 primeiras linhas),
// varrendo as abas e as ~10 primeiras linhas por uma que satisfaça `temCabecalho`.
function acharCabecalho(
  wb: XLSX.WorkBook,
  temCabecalho: (cols: string[]) => boolean
): { aoa: unknown[][]; hdr: number } | null {
  for (const nome of wb.SheetNames) {
    const aoa = XLSX.utils.sheet_to_json(wb.Sheets[nome], { header: 1, defval: '', raw: true }) as unknown[][];
    for (let i = 0; i < Math.min(10, aoa.length); i++) {
      const cols = (aoa[i] || []).map((v) => normKey(String(v)));
      if (temCabecalho(cols)) return { aoa, hdr: i };
    }
  }
  return null;
}

export async function importarVisitasHistoricas(file: File): Promise<ResumoImportVisitas> {
  const wb = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: 'array' });

  const temCheckin = (c: string) => c.replace(/[\s-]/g, '').includes('checkin');
  const achou = acharCabecalho(wb, (cols) => cols.some((c) => c.includes('cliente')) && cols.some(temCheckin));
  if (!achou) {
    throw new Error('Não encontrei a aba "Visitas" (precisa das colunas "Cliente" e "CheckIn").');
  }
  const { aoa, hdr } = achou;
  const cols = (aoa[hdr] as unknown[]).map((v) => normKey(String(v)));
  const iCli = cols.findIndex((c) => c.includes('cliente'));
  const iChk = cols.findIndex(temCheckin);

  const linhasRaw = (aoa.slice(hdr + 1) as unknown[][]).filter((r) =>
    (r || []).some((v) => String(v ?? '').trim() !== '')
  );

  // MAIOR data de visita por CODPARC.
  const maxByCodparc = new Map<string, number>();
  let semCodparc = 0;
  let semData = 0;
  for (const r of linhasRaw) {
    const mm = String(r[iCli] ?? '').match(/^\s*(\d+)\s*-/);
    const codparc = mm ? soDigitos(mm[1]) : '';
    if (!codparc) { semCodparc++; continue; }
    const dt = parseDataVisita(r[iChk]);
    if (!dt) { semData++; continue; }
    const ms = dt.getTime();
    const prev = maxByCodparc.get(codparc);
    if (prev == null || ms > prev) maxByCodparc.set(codparc, ms);
  }

  const linhas: LinhaVisita[] = Array.from(maxByCodparc.entries()).map(([codparc, ms]) => ({
    codparc,
    dataVisitaISO: new Date(ms).toISOString(),
  }));

  const fn = httpsCallable<
    { linhas: LinhaVisita[] },
    { casados: number; semMatch: number; atualizados: number; preservados: number }
  >(functions, 'importarVisitasHistoricas', { timeout: 300000 });
  const res = await fn({ linhas });
  const { casados, semMatch, atualizados, preservados } = res.data;
  return {
    total: linhasRaw.length,
    semCodparc,
    semData,
    codparcs: linhas.length,
    casados,
    semMatch,
    atualizados,
    preservados,
  };
}
